import { computed, ref } from 'vue';
import AnnouncementsAPI from 'dashboard/api/announcements';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export const REFRESH_JITTER_MS = 30000;
export const RETRY_BASE_MS = 30000;
export const RETRY_MAX_MS = 5 * 60000;
const MAX_TIMER_MS = 2 ** 31 - 1;
const SAFE_LINK = /^https?:\/\//i;

const withSafeLink = announcement => ({
  ...announcement,
  link: SAFE_LINK.test(announcement.link || '') ? announcement.link : null,
});

const toMs = value => (value ? Date.parse(value) : Number.NaN);

const isLiveAt = (announcement, at) => {
  const startsAt = toMs(announcement.starts_at);
  const endsAt = toMs(announcement.ends_at);
  if (startsAt > at) return false;
  return Number.isNaN(endsAt) || endsAt > at;
};

const fetched = ref([]);
const now = ref(Date.now());
const announcements = computed(() =>
  fetched.value.filter(a => isLiveAt(a, now.value))
);

let loadedAccountId = null;
let consumers = 0;
let refreshTimer = null;
let boundaryTimer = null;
let retryTimer = null;
let retryDelay = RETRY_BASE_MS;
let latestRequest = 0;
let stopped = false;

const randomDelay = maxMs => {
  const [value] = window.crypto.getRandomValues(new Uint32Array(1));
  return (value / 2 ** 32) * maxMs;
};

const nextBoundary = () => {
  const at = Date.now();
  return fetched.value
    .flatMap(a => [toMs(a.starts_at), toMs(a.ends_at)])
    .filter(ms => ms > at)
    .reduce((soonest, ms) => Math.min(soonest, ms), Infinity);
};

const scheduleBoundary = () => {
  clearTimeout(boundaryTimer);
  boundaryTimer = null;
  const boundary = nextBoundary();
  if (boundary === Infinity) return;

  boundaryTimer = setTimeout(
    () => {
      now.value = Date.now();
      scheduleBoundary();
    },
    Math.min(boundary - Date.now(), MAX_TIMER_MS)
  );
};

const scheduleRetry = retry => {
  if (retryTimer || stopped) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    retry().catch(() => {});
  }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
};

const fetchAnnouncements = async () => {
  latestRequest += 1;
  const request = latestRequest;
  const response = await AnnouncementsAPI.get().catch(() => null);
  if (request !== latestRequest) return;
  if (!response) {
    scheduleRetry(fetchAnnouncements);
    return;
  }
  retryDelay = RETRY_BASE_MS;
  fetched.value = (response.data?.announcements || []).map(withSafeLink);
  now.value = Date.now();
  scheduleBoundary();
};

const scheduleRefresh = () => {
  if (refreshTimer) return;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    fetchAnnouncements().catch(() => {});
  }, randomDelay(REFRESH_JITTER_MS));
};

const ensureAccount = accountId => {
  if (!accountId || accountId === loadedAccountId) return;
  loadedAccountId = accountId;
  fetched.value = [];
  fetchAnnouncements().catch(() => {});
};

const startListening = () => {
  consumers += 1;
  if (consumers > 1) return;
  stopped = false;
  emitter.on(BUS_EVENTS.ANNOUNCEMENTS_CHANGED, scheduleRefresh);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, scheduleRefresh);
  if (!loadedAccountId) {
    fetchAnnouncements().catch(() => {});
    return;
  }
  now.value = Date.now();
  scheduleBoundary();
};

const stopListening = () => {
  if (consumers === 0) return;
  consumers -= 1;
  if (consumers > 0) return;
  stopped = true;
  loadedAccountId = null;
  emitter.off(BUS_EVENTS.ANNOUNCEMENTS_CHANGED, scheduleRefresh);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, scheduleRefresh);
  clearTimeout(refreshTimer);
  clearTimeout(boundaryTimer);
  clearTimeout(retryTimer);
  refreshTimer = null;
  boundaryTimer = null;
  retryTimer = null;
};

export const useAnnouncements = () => ({
  announcements,
  fetchAnnouncements,
  ensureAccount,
  startListening,
  stopListening,
});
